import React, { useState } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { ChevronLeft, ExternalLink } from 'lucide-react';
import { toSnakeCase } from '../../utils/caseConversion';
import LanguageSelector from '../LanguageSelector';
import { Capacitor } from '@capacitor/core';
import { useLanguageContext } from '../../contexts/LanguageContext';
import LegalPageScreen from '../LegalPageScreen';

interface SignupScreenProps {
  onBack: () => void;
}

const SignupScreen: React.FC<SignupScreenProps> = ({ onBack }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showLegalPage, setShowLegalPage] = useState<'terms' | 'privacy' | null>(null);
  const isNative = Capacitor.isNativePlatform();
  const { language } = useLanguageContext();

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!acceptedTerms) {
      setError('Vous devez accepter les conditions d\'utilisation et la politique de confidentialité');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Create auth user with language for email template
      const { data, error: signupError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            username,
            lang: language, // Pass current language for email template
          },
          emailRedirectTo: `${window.location.origin}/${language}/auth/callback`,
        },
      });

      if (signupError) throw signupError;

      if (data.user) {
        console.log('✅ Utilisateur créé:', data.user.id);

        // 2. Créer manuellement le profil utilisateur
        // Attendre que la session soit active pour que RLS fonctionne
        try {
          // Attendre un court instant pour que la session soit établie
          await new Promise(resolve => setTimeout(resolve, 500));

          // Vérifier que la session est active
          const { data: sessionData } = await supabase.auth.getSession();
          if (!sessionData.session) {
            console.warn('⚠️ Session non active, profil sera créé au login');
          } else {
            const now = new Date().toISOString();
            const profileData = toSnakeCase({
              id: data.user.id,
              username,
              subscriptionTier: 'free',
              termsAcceptedAt: now,
              termsVersion: '1.0',
              privacyAcceptedAt: now,
              privacyVersion: '1.0',
              newsletterOptedIn: newsletterOptIn,
              newsletterOptedInAt: newsletterOptIn ? now : null,
            });

            const { error: profileError } = await supabase
              .from('user_profiles')
              .insert(profileData);

            if (profileError) {
              console.warn('⚠️ Erreur création profil (sera créé au login):', profileError);
            } else {
              console.log('✅ Profil créé avec succès');
            }
          }
        } catch (profileErr) {
          console.warn('⚠️ Profil non créé, sera créé au login');
        }

        // NOTE: Guest data migration removed - guests now have no persisted data
        // All user data starts fresh on account creation

        // Check if email confirmation is required (email_confirmed_at is null)
        if (!data.user.email_confirmed_at) {
          // Email needs confirmation - show success screen
          setSuccess(true);
          setLoading(false);
        } else {
          // Auto-login successful (no email confirmation required or already confirmed)
          console.log('✅ Connexion automatique réussie');
          // App.tsx will handle redirect via onAuthStateChange
        }
      }
    } catch (err: any) {
      console.error('❌ Erreur d\'inscription:', err);
      setError(err.message || 'Erreur lors de l\'inscription');
      setLoading(false);
    }
  };

  // Show legal page if requested
  if (showLegalPage) {
    return (
      <LegalPageScreen
        slug={showLegalPage}
        onBack={() => setShowLegalPage(null)}
      />
    );
  }

  if (success) {
    return (
      <div className="h-screen-adaptive bg-gradient-to-b from-purple-100 to-pink-100 flex items-center justify-center p-4 relative">
        {/* Language Selector - Top Right */}
        <div className={`absolute right-4 z-10 ${isNative ? 'top-0 safe-area-top pt-2' : 'top-4'}`}>
          <LanguageSelector />
        </div>

        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
          <div className="text-center">
            <div className="text-6xl mb-4">✅</div>
            <h2 className="text-2xl font-bold mb-4 font-title">Compte créé !</h2>
            <p className="text-gray-600 mb-6">
              Un email de confirmation a été envoyé à <strong>{email}</strong>.
              Veuillez vérifier votre boîte mail et cliquer sur le lien de confirmation.
            </p>
            <button
              onClick={onBack}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Retour à la connexion
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen-adaptive bg-gradient-to-b from-purple-100 to-pink-100 flex items-center justify-center p-4 relative">
      {/* Language Selector - Top Right */}
      <div className={`absolute right-4 z-10 ${isNative ? 'top-0 safe-area-top pt-2' : 'top-4'}`}>
        <LanguageSelector />
      </div>

      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-6 transition-colors"
        >
          <ChevronLeft size={20} />
          Retour
        </button>

        <h2 className="text-2xl font-bold text-center mb-6 font-title">
          Créer un compte
        </h2>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSignup} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom d'utilisateur
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="SuperHero123"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="votre@email.com"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mot de passe
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="••••••••"
              minLength={6}
              required
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">Minimum 6 caractères</p>
          </div>

          {/* Terms and Privacy acceptance */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="acceptTerms"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-1 w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
              disabled={loading}
            />
            <label htmlFor="acceptTerms" className="text-sm text-gray-600">
              J'accepte les{' '}
              <button
                type="button"
                onClick={() => setShowLegalPage('terms')}
                className="text-blue-600 hover:underline inline-flex items-center gap-1"
              >
                conditions d'utilisation
                <ExternalLink size={12} />
              </button>{' '}
              et la{' '}
              <button
                type="button"
                onClick={() => setShowLegalPage('privacy')}
                className="text-blue-600 hover:underline inline-flex items-center gap-1"
              >
                politique de confidentialité
                <ExternalLink size={12} />
              </button>
            </label>
          </div>

          {/* Newsletter opt-in */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="newsletterOptIn"
              checked={newsletterOptIn}
              onChange={(e) => setNewsletterOptIn(e.target.checked)}
              className="mt-1 w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
              disabled={loading}
            />
            <label htmlFor="newsletterOptIn" className="text-sm text-gray-600">
              Je souhaite recevoir les nouveautés et offres spéciales par email
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || !acceptedTerms}
            className="w-full bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Création...' : 'Créer mon compte'}
          </button>
        </form>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-600 text-center">
            Déjà un compte ?{' '}
            <button
              onClick={onBack}
              className="text-blue-600 hover:underline font-medium"
            >
              Se connecter
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupScreen;
